import React from "react";
import { motion } from "framer-motion";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";

// Define animation variants
const iconVariants = {
  initial: { scale: 0.8, opacity: 0 },
  animate: {
    scale: 1,
    opacity: 1,
    transition: { duration: 0.8, ease: "easeOut" },
  },
  hover: { scale: 1.1, transition: { duration: 0.3, ease: "easeInOut" } },
};

// Redesigned Geometric Icons

const WorkflowSteps: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);

  const workflowSteps = [
    {
      title: t("workflow.steps.0.title"),
      description: t("workflow.steps.0.description"),
      icon: (
        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 64 64"
          width="64"
          height="64"
          fill="none"
          stroke="#5D5D5D"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          variants={iconVariants}
          initial="initial"
          animate="animate"
          whileHover="hover"
        >
          {/* Document Body */}
          <motion.rect
            x="12"
            y="8"
            width="40"
            height="48"
            rx="4"
            animate={{
              transition: {
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              },
            }}
          />

          {/* Animated Lines Appearing and Fading Sequentially */}
          <motion.line
            x1="20"
            y1="20"
            x2="44"
            y2="20"
            strokeDasharray="24"
            strokeDashoffset="24"
            animate={{
              strokeDashoffset: [24, 0],
              opacity: [0, 1, 1, 0],
              transition: {
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.2,
              },
            }}
          />

          <motion.line
            x1="20"
            y1="30"
            x2="44"
            y2="30"
            strokeDasharray="24"
            strokeDashoffset="24"
            animate={{
              strokeDashoffset: [24, 0],
              opacity: [0, 1, 1, 0],
              transition: {
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.4,
              },
            }}
          />

          <motion.line
            x1="20"
            y1="40"
            x2="36"
            y2="40"
            strokeDasharray="16"
            strokeDashoffset="16"
            animate={{
              strokeDashoffset: [16, 0],
              opacity: [0, 1, 1, 0],
              transition: {
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.6,
              },
            }}
          />
        </motion.svg>
      ),
    },
    {
      title: t("workflow.steps.1.title"),
      description: t("workflow.steps.1.description"),
      icon: (
        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 64 64"
          width="64"
          height="64"
          fill="none"
          stroke="#5D5D5D"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          variants={iconVariants}
          initial="initial"
          animate="animate"
          whileHover="hover"
        >
          {/* Envelope Body */}
          <motion.rect
            x="12"
            y="22"
            width="40"
            height="28"
            rx="5"
            initial={{ opacity: 1 }}
            animate={{
              transition: {
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              },
            }}
          />

          {/* Envelope Flap Animation (Appears & Disappears) */}
          <motion.path
            d="M16 22 L32 14 L48 22"
            stroke="#5D5D5D"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0, 1, 0], // Appears & disappears
              transition: {
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              },
            }}
          />

          {/* Envelope Opening Line Animation */}
          <motion.line
            x1="20"
            y1="32"
            x2="40"
            y2="32"
            strokeDasharray="32"
            strokeDashoffset="32"
            animate={{
              strokeDashoffset: [32, 0],
              opacity: [0, 1, 1, 0],
              transition: {
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              },
            }}
          />
          {/* Envelope Opening Line Animation */}
          <motion.line
            x1="20"
            y1="38"
            x2="25"
            y2="38"
            strokeDasharray="32"
            strokeDashoffset="32"
            animate={{
              strokeDashoffset: [32, 0],
              opacity: [0, 1, 1, 0],
              transition: {
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              },
            }}
          />
        </motion.svg>
      ),
    },

    {
      title: t("workflow.steps.2.title"),
      description: t("workflow.steps.2.description"),
      icon: (
        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 64 64"
          width="64"
          height="64"
          fill="none"
          stroke="#5D5D5D"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          variants={iconVariants}
          initial="initial"
          animate="animate"
          whileHover="hover"
        >
          {/* Comparison Bars with Animated Fill */}
          <motion.rect
            x="8"
            y="16"
            width="12"
            height="32"
            rx="2"
            //fill="#b89ad1" // Light purple fill
            animate={{
              fill: ["#FFF", "#5D5D5D", "#b89ad1"], // Fill Animation
              transition: {
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              },
            }}
          />
          <motion.rect
            x="26"
            y="8"
            width="12"
            height="40"
            rx="2"
            animate={{
              fill: ["#5D5D5D", "#FFF", "#5D5D5D"],
              transition: {
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.3,
              },
            }}
          />
          <motion.rect
            x="44"
            y="20"
            width="12"
            height="28"
            rx="2"
            animate={{
              fill: ["#5D5D5D", "#b89ad1", "#FFF"],
              transition: {
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.6,
              },
            }}
          />
        </motion.svg>
      ),
    },
    {
      title: t("workflow.steps.3.title"),
      description: t("workflow.steps.3.description"),
      icon: (
        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 64 64"
          width="64"
          height="64"
          fill="none"
          stroke="#5D5D5D"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          variants={iconVariants}
          initial="initial"
          animate="animate"
          whileHover="hover"
        >
          {/* Box with Animated Fill */}
          <motion.rect
            x="12"
            y="12"
            width="40"
            height="40"
            rx="4"
            animate={{
              // fill: ["#b89ad1", "#644980", "#b89ad1"], // Filling animation
              transition: {
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              },
            }}
          />

          {/* Checkmark with Stroke Animation */}
          <motion.path
            d="M20 32l8 8 16-16"
            strokeDasharray="40"
            strokeDashoffset="40"
            animate={{
              strokeDashoffset: [40, 0], // Draws the checkmark smoothly
              transition: {
                duration: 1.2,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.5,
              },
            }}
          />
        </motion.svg>
      ),
    },
  ];
  return (
    <section className="flex flex-col items-center py-10 my-10">
      <h2 className="text-3xl md:text-3xl lg:text-3xl  font-bold mb-6 text-gray-800">
        {t("workflow.title")}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 px-6 w-full max-w-6xl">
        {workflowSteps.map((step, index) => (
          <motion.div
            key={index}
            className="p-6 flex flex-col items-center text-center transform transition-transform duration-300 hover:scale-105"
          >
            <div className="mb-4">{step.icon}</div>
            <h3 className="font-semibold text-lg text-gray-800">
              {step.title}
            </h3>
            <p className="text-sm text-gray-600">{step.description}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
};
export default WorkflowSteps;
